import type { CSSProperties } from 'react';

import { Skin, Sport } from '#src/__generated__/globalTypes.ts';
import { SetStellar } from '#src/atoms/icons/SetStellar.tsx';

import mobileDecoration from './assets/board/mobile-decoration.webp';
import footballComposeTeam from './assets/compose-team/FootballFieldBackground.webp';
import nbaComposeTeam from './assets/compose-team/NBAFieldBackground.webp';
import desktopNebulaBackground from './assets/desktopNebulaBackground.webp';
import playFootballBackgroundImage from './assets/footballBackground.webp';
import mobileNebulaBackground from './assets/mobileNebulaBackground.webp';
import playNbaBackgroundImage from './assets/nbaBackground.webp';
import onboardingBackground from './assets/onboarding-background.webp';
import packOpeningBackground from './assets/pack-opening-background.webp';
import replayDialogFootballBackground from './assets/replayDialog/football.webp';
import replayDialogNBABackground from './assets/replayDialog/nba.webp';
import footballDesktopCountdownBackground from './assets/seasonCountdown/desktopFootballBackground.webp';
import nbaDesktopCountdownBackground from './assets/seasonCountdown/desktopNBABackground.webp';
import seasonCountdownPacksFootball from './assets/seasonCountdown/packsFootball.webp';
import seasonCountdownPacksNBA from './assets/seasonCountdown/packsNBA.webp';
import seasonCountdownPlayersLeftFootball from './assets/seasonCountdown/playersLeftFootball.webp';
import seasonCountdownPlayersLeftNBA from './assets/seasonCountdown/playersLeftNBA.webp';
import seasonCountdownPlayersMobileFootball from './assets/seasonCountdown/playersMobileFootball.webp';
import seasonCountdownPlayersMobileNBA from './assets/seasonCountdown/playersMobileNBA.webp';
import seasonCountdownPlayersRightFootball from './assets/seasonCountdown/playersRightFootball.webp';
import seasonCountdownPlayersRightNBA from './assets/seasonCountdown/playersRightNBA.webp';
import setLogoImage from './assets/set-logo.svg';
import squadFootballBoard from './assets/squad/footballBoard.webp';
import squadTeamLogos from './assets/squad/team-logos.webp';
import commonPacksLogoImage from './assets/stellar-logo.svg';
import cardRevealSwipeIconImage from './assets/stellar-series-icon.svg';
import fullArtAv1 from './assets/videos/full-art.av1.webm';
import fullArtH264 from './assets/videos/full-art.h264.mp4';
import fullArtVp9 from './assets/videos/full-art.vp9.webm';
import holoAv1 from './assets/videos/holo.av1.webm';
import holoH264 from './assets/videos/holo.h264.mp4';
import holoVp9 from './assets/videos/holo.vp9.webm';
import jerseyNumberAv1 from './assets/videos/jersey-number.av1.webm';
import jerseyNumberH264 from './assets/videos/jersey-number.h264.mp4';
import jerseyNumberVp9 from './assets/videos/jersey-number.vp9.webm';
import meteorStrikerAv1 from './assets/videos/meteor-striker.av1.webm';
import meteorStrikerH264 from './assets/videos/meteor-striker.h264.mp4';
import meteorStrikerVp9 from './assets/videos/meteor-striker.vp9.webm';
import playVoucherIcon from './assets/voucher.svg';
import welcomeBackDialogBackground from './assets/welcomeBackDialogBackground.webp';
import welcomePackDiscounted from './assets/welcomePack/discounted.webp';

type SetPlaySportAssets = {
  backgroundImage: string;
};

const DEFAULT_SET_PLAY_SPORT_ASSETS: SetPlaySportAssets = {
  backgroundImage: playFootballBackgroundImage,
};

const SET_PLAY_SPORT_ASSETS: Partial<Record<Sport, SetPlaySportAssets>> = {
  [Sport.FOOTBALL]: DEFAULT_SET_PLAY_SPORT_ASSETS,
  [Sport.NBA]: {
    backgroundImage: playNbaBackgroundImage,
  },
};

const background = {
  background: `url(${desktopNebulaBackground}) center / cover no-repeat`,
  backgroundBlendMode: 'normal, normal',
};

export const SET_STELLAR = {
  displayName: 'Stellar',
  slug: 'stellar',
  colorTheme: undefined,
  supportedSports: [Sport.FOOTBALL, Sport.NBA] as const,
  cardEditionSlugs: [
    'stellar_standard_base',
    'stellar_shiny_base',
    'stellar_shiny_jersey_number',
    'stellar_shiny_meteor_striker',
    'stellar_holo_base',
    'stellar_holo_action_shot',
    'stellar_full_art_base',
    'stellar_full_art_signed',
  ] as const,
  skins: [Skin.STANDARD, Skin.SHINY, Skin.HOLO, Skin.FULL_ART],
  colors: {
    main: 'var(--c-set-stellar-surface-primary)',
    mainRGB: [225, 0, 255] satisfies [number, number, number],
    swipeEffectColor: '#7D41FF',
  },
  visual: {
    board: {
      mobileDecoration,
    },
    commonPacks: {
      logoImage: commonPacksLogoImage,
    },
    cardReveal: {
      swipeIconImage: cardRevealSwipeIconImage,
      background: {
        background: `
      url("${packOpeningBackground}") center / cover no-repeat
        `,
        backgroundBlendMode: 'normal',
      },
      videoSources: {
        holo: [
          { src: holoAv1, type: 'video/webm; codecs="av01.0.31M.08,opus"' },
          { src: holoVp9, type: 'video/webm; codecs="vp9"' },
          { src: holoH264 },
        ],
        jerseyNumber: [
          {
            src: jerseyNumberAv1,
            type: 'video/webm; codecs="av01.0.31M.08,opus"',
          },
          { src: jerseyNumberVp9, type: 'video/webm; codecs="vp9"' },
          { src: jerseyNumberH264 },
        ],
        meteorStriker: [
          {
            src: meteorStrikerAv1,
            type: 'video/webm; codecs="av01.0.31M.08,opus"',
          },
          { src: meteorStrikerVp9, type: 'video/webm; codecs="vp9"' },
          { src: meteorStrikerH264 },
        ],
        fullArt: [
          {
            src: fullArtAv1,
            type: 'video/webm; codecs="av01.0.31M.08,opus"',
          },
          { src: fullArtVp9, type: 'video/webm; codecs="vp9"' },
          { src: fullArtH264 },
        ],
      },
      newEffectColor: null,
    },
    seasonCountdown: {
      players: {
        [Sport.BASEBALL]: null,
        [Sport.NBA]: {
          mobile: seasonCountdownPlayersMobileNBA,
          desktopLeft: seasonCountdownPlayersLeftNBA,
          desktopRight: seasonCountdownPlayersRightNBA,
        },
        [Sport.FOOTBALL]: {
          mobile: seasonCountdownPlayersMobileFootball,
          desktopLeft: seasonCountdownPlayersLeftFootball,
          desktopRight: seasonCountdownPlayersRightFootball,
        },
      } satisfies Record<Sport, Record<string, string> | null>,
      backgroundMobile: {
        background: `url("${mobileNebulaBackground}") center / cover no-repeat`,
      },
      backgroundDesktop: {
        [Sport.BASEBALL]: undefined,
        [Sport.NBA]: {
          background: `url("${nbaDesktopCountdownBackground}") center / cover no-repeat`,
        },
        [Sport.FOOTBALL]: {
          background: `url("${footballDesktopCountdownBackground}") center / cover no-repeat`,
        },
      } satisfies Record<Sport, CSSProperties | undefined>,
      packs: {
        [Sport.BASEBALL]: null,
        [Sport.NBA]: seasonCountdownPacksNBA,
        [Sport.FOOTBALL]: seasonCountdownPacksFootball,
      } satisfies Record<Sport, string | null>,
    },
    brand: {
      logoImage: setLogoImage,
      SetLogoImg: null,
      SetIcon: SetStellar,
    },
    play: {
      getSportAssets: (sport: Sport) =>
        SET_PLAY_SPORT_ASSETS[sport] || DEFAULT_SET_PLAY_SPORT_ASSETS,
      voucherIcon: playVoucherIcon,
    },
    replayDialog: {
      background: {
        [Sport.FOOTBALL]: replayDialogFootballBackground,
        [Sport.NBA]: replayDialogNBABackground,
        [Sport.BASEBALL]: undefined,
      },
    },
    welcomePackDialog: {
      regular: {
        ...background,
      },
      discounted: {
        background: `url(${welcomePackDiscounted}) center / cover no-repeat, var(--c-set-surface-primary)`,
      },
    },
    welcomeBackDialog: {
      background: `url(${welcomeBackDialogBackground}) center / cover no-repeat`,
    },
    composeTeam: {
      [Sport.FOOTBALL]: footballComposeTeam,
      [Sport.NBA]: nbaComposeTeam,
      [Sport.BASEBALL]: undefined,
    },
    squad: {
      boardBackground: {
        [Sport.FOOTBALL]: squadFootballBoard,
        [Sport.NBA]: squadFootballBoard,
        [Sport.BASEBALL]: undefined,
      },
      teamLogos: squadTeamLogos,
    },
    onboardingBackground: {
      background: `
        linear-gradient(
          to bottom,
          rgba(var(--c-rgb-set-surface-tertiary), 0.7),
          rgba(var(--c-rgb-set-surface-tertiary), 0.7)
        ),
        url(${onboardingBackground}) no-repeat center / cover`,
      backgroundBlendMode: 'multiply, normal',
    },
  },
  background,
  showTeamFilters: false,
  showDefaultCollectionTaskDescription: false,
} as const;
